// src/queries/pages/dashboard/propertyComplianceApiConfig.js

import { STALE_TIME, GC_TIME, QUERY_PRESETS } from "@/queries/queryTimeConfig";

export const propertyComplianceConfig = {
  /* -------------------------------------------------------------------------- */
  /*                               FETCH OPERATIONS                             */
  /* -------------------------------------------------------------------------- */

  getDocumentTypes: {
    key: "getDocumentTypes",
    url: "/api/v1/property-compliance/document-types",
    method: "GET",
    options: {
      staleTime: STALE_TIME.STATIC,
      gcTime: GC_TIME.VERY_LONG,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch available document types" },
    },
  },

  getComplianceByRegUserId: {
    key: "getComplianceByRegUserId",
    url: (params) => `/api/v1/property-compliance/registration/${params?.regUserId || params?.id || params}`,
    method: "GET",
    options: {
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.LONG,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch compliance documents by registration ID" },
    },
  },

  getTempComplianceByRegUserId: {
    key: "getTempComplianceByRegUserId",
    url: (params) => `/api/v1/property-compliance/registration/${params?.regUserId || params?.id || params}`,
    method: "GET",
    options: {
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.LONG,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch compliance documents by registration ID" },
    },
  },

  getComplianceForReupload: {
    key: "getComplianceForReupload",
    url: "/api/v1/property-compliance/reupload",
    method: "GET",
    options: {
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.LONG,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch compliance documents for reupload" },
    },
  },

  getTempComplianceForReupload: {
    key: "getTempComplianceForReupload",
    url: "/api/v1/property-compliance/reupload",
    method: "GET",
    options: {
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.LONG,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch compliance documents for reupload" },
    },
  },

  // These endpoints don't exist in pms-api yet — kept for future use
  getPendingCompliance: {
    key: "getPendingCompliance",
    url: "/api/v1/property-compliance/pending",
    method: "GET",
    options: {
      ...QUERY_PRESETS.REALTIME,
      refetchOnReconnect: true,
      retry: 2,
      meta: { description: "Fetch pending compliance documents" },
    },
  },

  getComplianceDocsForReview: {
    key: "getComplianceDocsForReview",
    url: (params) => `/api/v1/property-compliance/by-code/${params?.uniqueCode || params}`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.REALTIME,
      refetchOnReconnect: true,
      retry: 2,
      meta: { description: "Fetch compliance documents for review by uniqueCode" },
    },
  },

  getTempComplianceDocsForReview: {
    key: "getTempComplianceDocsForReview",
    url: (params) => `/api/v1/property-compliance/by-code/${params?.uniqueCode || params}`,
    method: "GET",
    options: {
      ...QUERY_PRESETS.REALTIME,
      refetchOnReconnect: true,
      retry: 2,
      meta: { description: "Fetch all compliance documents for review" },
    },
  },

  getPropertyComplianceByOrgId: {
    key: "getPropertyComplianceByOrgId",
    url: "/api/v1/property-compliance",
    method: "GET",
    options: {
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.STATIC,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch property compliance by organization ID" },
    },
  },

  getPermanentComplianceByRegUserId: {
    key: "getPermanentComplianceByRegUserId",
    url: (params) => `/api/v1/property-compliance/registration/${params?.regUserId || params?.id || params}`,
    method: "GET",
    options: {
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.STATIC,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch permanent compliance documents by registration ID" },
    },
  },

  getAllPropertyCompliance: {
    key: "getAllPropertyCompliance",
    url: "/api/v1/property-compliance",
    method: "GET",
    options: {
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.EXTENDED,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch all property compliance (admin only)" },
    },
  },

  getPropertyComplianceWithOrganization: {
    key: "getPropertyComplianceWithOrganization",
    url: "/api/v1/property-compliance",
    method: "GET",
    options: {
      staleTime: STALE_TIME.LONG,
      gcTime: GC_TIME.STATIC,
      refetchOnWindowFocus: false,
      retry: 2,
      meta: { description: "Fetch compliance with organization details" },
    },
  },

  /* -------------------------------------------------------------------------- */
  /*                            MUTATION OPERATIONS                             */
  /* -------------------------------------------------------------------------- */

  createPropertyCompliance: {
    key: "createPropertyCompliance",
    url: "/api/v1/property-compliance/submit",
    method: "POST",
    options: {
      invalidateKeys: [
        "getComplianceByRegUserId",
        "getTempComplianceByRegUserId",
        "getPendingCompliance",
        "getComplianceForReupload",
        "getTempComplianceForReupload",
        "getPropertyOwnerRegistrations",
      ],
      meta: { description: "Submit property compliance documents" },
    },
  },

  createTempPropertyCompliance: {
    key: "createTempPropertyCompliance",
    url: "/api/v1/property-compliance/submit",
    method: "POST",
    options: {
      invalidateKeys: [
        "getComplianceByRegUserId",
        "getTempComplianceByRegUserId",
        "getPendingCompliance",
        "getComplianceForReupload",
        "getTempComplianceForReupload",
        "getPropertyOwnerRegistrations",
      ],
      meta: { description: "Submit property compliance documents" },
    },
  },

  updateDocumentStatus: {
    key: "updateDocumentStatus",
    url: (data) => `/api/v1/property-compliance/${data?.id}/documents/${data?.docId}/review`,
    method: "PATCH",
    options: {
      invalidateKeys: [
        "getComplianceDocsForReview",
        "getTempComplianceDocsForReview",
        "getComplianceByRegUserId",
        "getTempComplianceByRegUserId",
        "getPropertyOwnerRegistrations",
      ],
      meta: { description: "Review a compliance document" },
    },
  },

  updatePermanentDocumentStatus: {
    key: "updatePermanentDocumentStatus",
    url: (data) => `/api/v1/property-compliance/${data?.id}/documents/${data?.docId}/review`,
    method: "PATCH",
    options: {
      invalidateKeys: [
        "getAllPropertyCompliance",
        "getPropertyComplianceByOrgId",
        "getPermanentComplianceByRegUserId",
        "getComplianceByRegUserId",
        "getPropertyComplianceWithOrganization",
      ],
      meta: { description: "Review a permanent compliance document" },
    },
  },
};
